import { Box } from '@quintoandar-tokko/jopijs';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import styles from './charts.module.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

ChartJS.defaults.animation = false;
ChartJS.defaults.responsiveAnimationDuration = 0;

export default function GenericLineChart({
  labels,
  datasets,
  onPointClick,
  getTooltipLabel,
  showLegend = false,
  yStepSize,
  autoSkip = false,
  yTitle = '',
  tooltipSuffix = '',
}) {
  const normalizedLabels = labels.map((label) =>
    typeof label === 'string' ? label : label.name
  );

  const chartData = {
    labels: normalizedLabels,
    datasets: datasets.map((dataset) => ({
      label: dataset.label,
      data: dataset.data,
      tension: 0,
      fill: false,
      borderWidth: 2,
      backgroundColor: 'rgba(26, 73, 88, 0.1)',
      borderColor: dataset.borderColor ?? '#427F94',

      pointBorderColor: dataset.pointBorderColor ?? '#427F94',
      pointBackgroundColor: dataset.pointBackgroundColor ?? '#427F94',
      pointHoverBackgroundColor: dataset.pointHoverBackgroundColor ?? '#427F94',
      pointRadius: dataset.pointRadius ?? 0,
      pointHoverRadius: dataset.pointHoverRadius ?? 0,
    })),
  };

  function handleHover(event) {
    const hasClickable = !!onPointClick;
    event.native.target.style.cursor = hasClickable ? 'pointer' : 'default';
  }

  function handleClick(_, elements) {
    if (!elements.length || !onPointClick) return;
    const { datasetIndex, index } = elements[0];
    const dataset = chartData.datasets[datasetIndex];
    const label = labels[index];
    const value = dataset.data[index];

    onPointClick({ dataset, label, value, datasetIndex, index });
  }

  function handleTooltip(context) {
    if (typeof getTooltipLabel === 'function') {
      return getTooltipLabel(context);
    }

    const label = context.dataset.label || '';
    const value = context.raw;

    const suffix = tooltipSuffix ? ` ${tooltipSuffix}` : '';
    return `${label}: ${value}${suffix}`;
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    transitions: {
      active: {
        animation: {
          duration: 0,
        },
      },
      resize: {
        duration: 0,
      },
    },
    plugins: {
      title: { display: false },
      legend: { display: showLegend },
      tooltip: {
        callbacks: {
          animation: false,
          label: handleTooltip,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          autoSkip,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: yStepSize ?? undefined,
          precision: 0,
        },
      },
    },
    onHover: handleHover,
    onClick: handleClick,
  };

  return (
    <Box className={styles.lineChartContainer}>
      {yTitle && (
        <Box as="span" className={styles.lineChartYTitle}>
          {yTitle}
        </Box>
      )}

      <Box className={styles.lineChartInnerWrapper}>
        <Line options={options} data={chartData} />
      </Box>
    </Box>
  );
}
